import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay,
  eachDayOfInterval, format, isSameMonth, isSameDay, isWithinInterval,
  isBefore, isAfter, parseISO,
} from "date-fns";

import "./rangeCalendar.css";

/**
 * A two-month calendar range picker, in a popover.
 *
 * Future days are disabled — a custom range can only cover dates that already
 * have (or could have) scores. Each panel has its own month/year selectors.
 */

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const toISO = (d) => format(d, "yyyy-MM-dd");
const parse = (v) => {
  if (!v) return null;
  try { const d = parseISO(v); return Number.isNaN(d.getTime()) ? null : d; } catch (e) { return null; }
};

const todayStart = () => startOfDay(new Date());
const isFutureDay = (d) => isAfter(startOfDay(d), todayStart());

const Month = ({ month, years, from, to, hover, onMonth, onYear, onPick, onHover }) => {
  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(month));
    const gridEnd = endOfWeek(endOfMonth(month));
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [month]);

  const inRange = (d) => {
    const end = to || hover;
    if (!from || !end) return false;
    const [a, b] = isBefore(end, from) ? [end, from] : [from, end];
    return isWithinInterval(d, { start: a, end: b });
  };

  return (
    <div className="rc-month">
      <div className="rc-mhead">
        <select
          className="rc-sel"
          value={month.getMonth()}
          onChange={(e) => onMonth(Number(e.target.value))}
          aria-label="Month"
        >
          {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
        </select>
        <select
          className="rc-sel rc-sel-y"
          value={month.getFullYear()}
          onChange={(e) => onYear(Number(e.target.value))}
          aria-label="Year"
        >
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div className="rc-dow">{DOW.map((d) => <span key={d}>{d[0]}</span>)}</div>
      <div className="rc-grid">
        {days.map((d) => {
          const dim = !isSameMonth(d, month);
          const future = isFutureDay(d);
          const isEnd = (from && isSameDay(d, from)) || (to && isSameDay(d, to));
          const cls = [
            "rc-day",
            dim ? "rc-dim" : "",
            future ? "rc-future" : "",
            !future && inRange(d) ? "rc-in" : "",
            !future && isEnd ? "rc-end" : "",
          ].filter(Boolean).join(" ");
          return (
            <button
              type="button"
              key={toISO(d)}
              className={cls}
              disabled={future}
              aria-disabled={future}
              onClick={() => { if (!future) onPick(d); }}
              onMouseEnter={() => { if (!future) onHover(d); }}
            >
              {format(d, "d")}
            </button>
          );
        })}
      </div>
    </div>
  );
};
Month.propTypes = {
  month: PropTypes.instanceOf(Date).isRequired,
  years: PropTypes.arrayOf(PropTypes.number).isRequired,
  from: PropTypes.instanceOf(Date),
  to: PropTypes.instanceOf(Date),
  hover: PropTypes.instanceOf(Date),
  onMonth: PropTypes.func.isRequired,
  onYear: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  onHover: PropTypes.func.isRequired,
};
Month.defaultProps = { from: null, to: null, hover: null };

const RangeCalendar = ({ value, onApply, onClose }) => {
  const ref = useRef(null);
  const [from, setFrom] = useState(parse(value && value.from));
  const [to, setTo] = useState(parse(value && value.to));
  const [hover, setHover] = useState(null);

  /* Each panel tracks its own month, set independently. */
  const initLeft = startOfMonth(from || new Date());
  const [leftM, setLeftM] = useState(initLeft);
  const [rightM, setRightM] = useState(
    startOfMonth(new Date(initLeft.getFullYear(), initLeft.getMonth() + 1, 1)),
  );

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    const out = [];
    // No future years — scores only exist through today.
    for (let y = now - 6; y <= now; y += 1) out.push(y);
    return out;
  }, []);

  /* Keep internal selection in sync when the parent range changes (Apply on
     another open, or clearing after a preset switch). Mount-only state used
     to keep painting the previous from/to after the prop already moved. */
  const valueFrom = (value && value.from) || "";
  const valueTo = (value && value.to) || "";
  useEffect(() => {
    const nextFrom = parse(valueFrom || null);
    const nextTo = parse(valueTo || null);
    setFrom(nextFrom);
    setTo(nextTo);
    setHover(null);
    const left = startOfMonth(nextFrom || new Date());
    setLeftM(left);
    setRightM(startOfMonth(new Date(left.getFullYear(), left.getMonth() + 1, 1)));
  }, [valueFrom, valueTo]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const pick = (d) => {
    if (isFutureDay(d)) return;
    if (!from || (from && to)) { setFrom(d); setTo(null); return; }
    if (isBefore(d, from)) { setTo(from); setFrom(d); } else { setTo(d); }
  };

  let label = "Pick a start day";
  if (from && to) label = `${format(from, "d MMM yyyy")} – ${format(to, "d MMM yyyy")}`;
  else if (from) label = `${format(from, "d MMM yyyy")} – pick an end day`;

  const canApply = from && to && !isFutureDay(from) && !isFutureDay(to);

  return (
    <div className="rc" ref={ref} role="dialog" aria-label="Choose a date range">
      <div className="rc-cal">
        <div className="rc-picked">{label}</div>
        <div className="rc-hint">Dates after today can’t be selected.</div>

        <div className="rc-months" onMouseLeave={() => setHover(null)}>
          <Month
            month={leftM}
            years={years}
            from={from}
            to={to}
            hover={hover}
            onMonth={(m) => setLeftM(new Date(leftM.getFullYear(), m, 1))}
            onYear={(y) => setLeftM(new Date(y, leftM.getMonth(), 1))}
            onPick={pick}
            onHover={setHover}
          />
          <Month
            month={rightM}
            years={years}
            from={from}
            to={to}
            hover={hover}
            onMonth={(m) => setRightM(new Date(rightM.getFullYear(), m, 1))}
            onYear={(y) => setRightM(new Date(y, rightM.getMonth(), 1))}
            onPick={pick}
            onHover={setHover}
          />
        </div>

        <div className="rc-foot">
          <button type="button" className="rc-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="rc-btn primary"
            disabled={!canApply}
            onClick={() => canApply && onApply({ from: toISO(from), to: toISO(to) })}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

RangeCalendar.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.object,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
RangeCalendar.defaultProps = { value: null };

export default RangeCalendar;
